import clsx from 'clsx'
import { ArrowRightIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'

import { Button, useFocusTransition } from 'slab'

interface RatingProps<Scale = string> {
  name: string;
  onSubmit?: (value: Scale) => Promise<void> | void;
  scale: { [value: string]: Scale };
}

export function Rating ({ name, onSubmit, scale }: RatingProps) {
  const [option, setOption] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useFocusTransition()

  async function handleSubmit (value: string) {
    setSubmitting(true)
    await onSubmit?.(value)
    setSubmitting(false)
  }

  return (
    <motion.div
      className='mt-6'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          handleSubmit(option as string)
        }}
      >
        <fieldset className='flex flex-col items-center'>
          <legend className='sr-only'>Select one</legend>
          <div className='flex flex-col gap-2 w-full'>
            {Object.entries(scale).map(([value, label], index) => (
              <label
                key={value}
                className={clsx(
                  'relative rounded-lg flex items-center justify-center px-2 py-1 body text-center bg-gray-100 hover:bg-gray-200 transition cursor-pointer outline outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-thatch-500',
                  {
                    'has-[:checked]:bg-thatch-600 has-[:checked]:text-white has-[:checked]:border-emphasis':
                      index === 0,
                    'has-[:checked]:bg-gray-600 has-[:checked]:text-white':
                      index !== 0,
                  }
                )}
                onClick={() => {
                  handleSubmit(value)
                }}
              >
                <input
                  className='sr-only'
                  name={name}
                  onClick={(event) => {
                    event.stopPropagation()
                  }}
                  onChange={({ target }) => {
                    setOption(target.value)
                  }}
                  type='radio'
                  value={value}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <motion.div
          className='flex items-center justify-center w-full'
          animate={option ? 'visible' : 'hidden'}
          initial='hidden'
          variants={{
            hidden: { marginTop: 0, opacity: 0, height: 0, pointerEvents: 'none' },
            visible: { marginTop: 12, opacity: 1, height: 'auto', pointerEvents: 'auto' },
          }}
        >
          <Button
            disabled={!option || submitting}
            rightIcon={ArrowRightIcon}
            type='submit'
            variant='primary'
            width='full'
          >
            Next
          </Button>
        </motion.div>
      </form>
    </motion.div>
  )
}

Rating.displayName = 'InProductFeedback.Rating'
